// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { fetch } from "@insite/client-framework/ServerSideRendering";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Download from "@store/Library/Components/Toolkit/Icons/Download";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import Accordion from "@store/MobiusOverrides/Accordion/Accordion";
import Modal, { ModalProps } from "@store/MobiusOverrides/Modal/Modal";
import { accordionInfoSectionProps } from "@store/Themes/common/props/accordionInfo";
import { convertFileToBase64 } from "@store/Framework/Common/Utilities/convertFileToBase64";
import constants from "@store/constants";
import clearUploadedFileStatus from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/ClearFileStatus";
import clearSubmittedUploadedOrder from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/ClearSubmittedOrder";
import createCartUpload from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/CreateCartUpload";
import setErrorsModalIsOpen from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/SetErrorsModalIsOpen";
import setIsBadFile from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/SetUploadLimitExceeded";
import setCanSubmitOrder from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/SetCanSubmitOrder";
import submitUploadedOrder from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/SubmitUploadedOrder";
import uploadAndValidateOrder from "@store/Framework/Store/Greif/Components/GreifOrderUpload/Handlers/UploadAndValidateFile";
import GreifOrderUploadErrorsModal from "@store/Library/Components/GreifOrderUploadErrorsModal";
import AccordionSection, {
    AccordionSectionPresentationProps,
} from "@store/MobiusOverrides/AccordionSection/AccordionSection";
import LoadingOverlay from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useContext, useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    templateFilePath = "templateFilePath",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.templateFilePath]: string;
    };
}

export interface OrderUploadFlowStyles {
    outerWrapper?: InjectableCss;
    successBox?: InfosBoxProps;
    errorBox?: InfosBoxProps;
    stepsWrapper?: InjectableCss;
    stepRow?: InjectableCss;
    stepIndicator?: TypographyPresentationProps;
    stepContent?: InjectableCss;
    stepHeading?: TypographyPresentationProps;
    stepDescription?: TypographyPresentationProps;
    downloadButton?: ButtonPresentationProps;
    accordion?: AccordionSectionPresentationProps;
    accordionFieldText?: TypographyPresentationProps;
    fileUploader?: FileUploadPresentationProps;
    fileErrorWrapper?: InjectableCss;
    fileErrorBox?: InfosBoxProps;
    fileErrorText?: TypographyPresentationProps;
    downloadFaultyFileButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    confirmModal?: ModalProps;
    confirmModalActions?: InjectableCss;
    confirmModalCancelButton?: ButtonPresentationProps;
    confirmModalSubmitButton?: ButtonPresentationProps;
}

export const orderUploadFlowStyles: OrderUploadFlowStyles = {
    outerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(16)};
        `,
    },
    successBox: { type: "success" },
    errorBox: { type: "error" },
    stepsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    stepRow: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: ${pxToRem(10)};
            padding: ${pxToRem(32)} 0;
            border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};

            &:first-child {
                padding-top: 0;
            }

            &:last-child {
                border-bottom: 0;
                padding-bottom: 0;
            }
        `,
    },
    stepIndicator: {
        css: css`
            display: flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            width: ${pxToRem(28)};
            height: ${pxToRem(28)};
            border-radius: 50%;
            background-color: ${getColor("common.accent")};
            color: ${getColor("primary")};
            border: ${pxToRem(1)} solid ${getColor("primary")};
            font-size: ${x => x.theme.design.typography.size.base};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    stepContent: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(12)};
            flex: 1;
        `,
    },
    stepHeading: {
        variant: "h4",
        forwardAs: "h3",
    },
    stepDescription: {
        variant: "p",
    },
    downloadButton: {
        variant: "tertiary",
        icon: {
            src: Download,
            position: "left",
        },
    },
    accordion: {
        mergeCss: true,
        ...accordionInfoSectionProps,
    },
    accordionFieldText: {
        variant: "p",
        css: css`
            padding: ${pxToRem(16)} ${pxToRem(24)};
        `,
    },
    fileUploader: {
        buttonProps: {
            variant: "tertiary",
            css: css`
                ${x => x.theme.button.tertiary.css}
            `,
        },
    },
    fileErrorWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(8)};
        `,
    },
    fileErrorBox: {
        type: "error",
        size: "small",
    },
    fileErrorText: {},
    downloadFaultyFileButton: {
        variant: "tertiary",
        icon: {
            src: Download,
            position: "left",
        },
    },
    submitButton: {
        sizeVariant: "large",
        css: css`
            ${x => x.theme.button.primary.css}
        `,
    },
    confirmModal: {
        sizeVariant: "medium",
    },
    confirmModalActions: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: ${pxToRem(10)};
            padding-top: ${pxToRem(16)};
            flex-direction: row;
        `,
    },
    confirmModalCancelButton: {
        variant: "tertiary",
    },
    confirmModalSubmitButton: {
        variant: "primary",
    },
};

const styles = orderUploadFlowStyles;

const OrderUploadFlow: FC<OwnProps> = ({ fields: { templateFilePath } }) => {
    const dispatch = useAppDispatch();
    const toasterContext = useContext(ToasterContext);

    const orderUploadState = useAppSelector(state => state.greif.components.greifOrderUpload);
    const signInUrl = useAppSelector(state => getPageLinkByPageType(state, "SignInPage")?.url);

    const {
        isUploading,
        isCreatingCart,
        isSubmitting,
        isBadFile,
        uploadLimitExceeded,
        errorMessage,
        errors,
        document: returnedDocument,
        fileUploaded,
        cartIsCreated,
        webOrderNumber,
        creationFailed,
    } = orderUploadState;

    const [file, setFile] = useState<File | null>(null);
    const [incorrectFileExtension, setIncorrectFileExtension] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [orderSubmitted, setOrderSubmitted] = useState(false);
    const hasErrorsInFile = errors.length > 0;

    /*
     * Clear upload/order state on unmount (user navigates away).
     */
    useEffect(() => {
        return () => {
            dispatch(clearUploadedFileStatus({}));
            dispatch(clearSubmittedUploadedOrder({}));
        };
    }, []);

    /* After a successful upload+validate with no row errors, open the confirmation modal */
    useEffect(() => {
        if (!fileUploaded) {
            return;
        }
        if (!hasErrorsInFile) {
            dispatch(setCanSubmitOrder({}));
            setIsConfirmModalOpen(true);
        }
    }, [fileUploaded]);

    /* After cart is created, submit the order and clean up */
    useEffect(() => {
        if (!cartIsCreated) {
            return;
        }
        dispatch(submitUploadedOrder({ documents: [returnedDocument], webOrderNumber }));
        dispatch(clearUploadedFileStatus({}));
        setFile(null);
        setOrderSubmitted(true);
        toasterContext.addToast({
            body: translate("Order {0} submitted.", `# ${webOrderNumber}`),
            messageType: "success",
        });
    }, [cartIsCreated]);

    /* Open errors modal when API-level errors arise */
    useEffect(() => {
        if (isBadFile || uploadLimitExceeded || errorMessage) {
            dispatch(setErrorsModalIsOpen({ errorsModalIsOpen: true }));
        }
    }, [isBadFile, uploadLimitExceeded, errorMessage]);

    /* Reset upload state when file is cleared */
    useEffect(() => {
        if (!file) {
            dispatch(clearUploadedFileStatus({}));
            setIncorrectFileExtension(false);
            dispatch(setIsBadFile({ isBadFile: false }));
            dispatch(setUploadLimitExceeded({ uploadLimitExceeded: false }));
        }
    }, [file]);

    const handleTemplateDownload = () => {
        const fileName = templateFilePath.split("/").pop() ?? "template";
        fetch(templateFilePath)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to download template: ${response.status}`);
                }
                return response.blob();
            })
            .then(data => {
                const url = window.URL.createObjectURL(data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
    };

    const handleFileChange = async (event: React.ChangeEvent<any>) => {
        if (cartIsCreated) {
            dispatch(clearSubmittedUploadedOrder({}));
        }
        setOrderSubmitted(false);

        const uploadedFile: File | undefined = event.target.files?.[0];

        if (uploadedFile !== file) {
            dispatch(clearUploadedFileStatus({}));
        }

        setFile(uploadedFile ?? null);
        if (uploadedFile) {
            const ext = getFileExtension(uploadedFile.name);
            const incorrect = ["xls", "xlsx", "csv"].indexOf(ext) === -1;
            setIncorrectFileExtension(incorrect);
            dispatch(setIsBadFile({ isBadFile: incorrect }));
            dispatch(setUploadLimitExceeded({ uploadLimitExceeded: false }));
        }
        event.target.value = "";
    };

    const handleSubmitOrder = async () => {
        if (!file) {
            return;
        }
        const base64Data = (await convertFileToBase64(file)) as string;
        const fileData = base64Data
            .replace("-", "+")
            .replace("_", "/")
            .match(/(?<=base64,).*/);
        if (!fileData) {
            return;
        }
        dispatch(
            uploadAndValidateOrder({
                documents: [{ fileName: file.name, data: fileData[0] }],
                onError: (error: any) => {
                    if (error?.statusCode === 401) {
                        window.location.href = signInUrl!;
                    } else {
                        toasterContext.addToast({
                            body: translate("An error occurred. Try again later."),
                            messageType: "danger",
                        });
                    }
                },
            }),
        );
    };

    /* Shared by the modal's Cancel button and its native close (X) control so both fully
     * reset the upload state; otherwise fileUploaded stays true and re-submitting the same
     * file never re-triggers the confirmation modal (its effect only fires on a real change). */
    const handleCancelConfirm = () => {
        setIsConfirmModalOpen(false);
        dispatch(clearUploadedFileStatus({}));
    };

    const handleConfirmSubmit = () => {
        setIsConfirmModalOpen(false);
        dispatch(
            createCartUpload({
                onError: (error: any) => {
                    if (error?.statusCode === 401) {
                        window.location.href = signInUrl!;
                    } else {
                        toasterContext.addToast({
                            body: translate("An error occurred. Try again later."),
                            messageType: "danger",
                            timeoutLength: 4000,
                        });
                    }
                },
            }),
        );
    };

    const handleDownloadFaultyFile = () => {
        const fileURL = `data:${constants.fileType.xlsx};base64,${returnedDocument.data}`;
        fetch(fileURL)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to download file: ${response.status}`);
                }
                return response.blob();
            })
            .then(data => {
                const url = window.URL.createObjectURL(data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", returnedDocument.fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
    };

    const isLoading = isUploading || isCreatingCart || isSubmitting;
    const isSubmitDisabled = !file || incorrectFileExtension || isLoading;

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledWrapper {...styles.outerWrapper}>
                {orderSubmitted && (
                    <InfosBox {...styles.successBox}>
                        {translate(
                            "Order {0} submitted. You will receive a confirmation email shortly.",
                            `# ${webOrderNumber}`,
                        )}
                    </InfosBox>
                )}
                {creationFailed && (
                    <InfosBox {...styles.errorBox}>
                        {translate("An error occurred while submitting your order. Please try again.")}
                    </InfosBox>
                )}
                <StyledWrapper {...styles.stepsWrapper}>
                    {/* Step 1 — Download the order template */}
                    <StyledWrapper {...styles.stepRow}>
                        <Typography {...styles.stepIndicator} aria-hidden="true">
                            1
                        </Typography>
                        <StyledWrapper {...styles.stepContent}>
                            <Typography {...styles.stepHeading}>{translate("Download the order template")}</Typography>
                            <Typography {...styles.stepDescription}>
                                {translate(
                                    "The Excel template is structured to conveniently accommodate the input of item numbers, quantities, delivery dates, and PO numbers. Ensure all information is accurately filled out to avoid processing delays.",
                                )}
                            </Typography>
                            <div>
                                <Button
                                    {...styles.downloadButton}
                                    onClick={handleTemplateDownload}
                                    data-test-selector="orderUploadFlow_downloadTemplate"
                                    disabled={!templateFilePath}
                                >
                                    {translate("Download Template")}
                                </Button>
                            </div>
                        </StyledWrapper>
                    </StyledWrapper>

                    {/* Step 2 — Complete the template */}
                    <StyledWrapper {...styles.stepRow}>
                        <Typography {...styles.stepIndicator} aria-hidden="true">
                            2
                        </Typography>
                        <StyledWrapper {...styles.stepContent}>
                            <Typography {...styles.stepHeading}>
                                {translate("Fill in item numbers, quantities, dates, PO numbers")}
                            </Typography>
                            <Accordion headingLevel={3}>
                                <AccordionSection
                                    {...styles.accordion}
                                    title={translate("What goes in each field?")}
                                    uid="orderUploadFlowAccordion"
                                >
                                    <Typography {...styles.accordionFieldText}>
                                        <strong>{translate("Item Numbers:")}</strong>{" "}
                                        {translate(
                                            "List all item numbers for the products being ordered. The user may enter the Greif item number or an available customer item number.",
                                        )}
                                        <br />
                                        <strong>{translate("Quantities:")}</strong>{" "}
                                        {translate("Enter the required quantity for each item.")}
                                        <br />
                                        <strong>{translate("Delivery Dates:")}</strong>{" "}
                                        {translate("Enter the requested delivery date for each item.")}
                                        <br />
                                        <strong>{translate("PO Numbers:")}</strong>{" "}
                                        {translate("Enter the Purchase Order number associated with each item.")}
                                        <br />
                                        <strong>{translate("Notes:")}</strong>{" "}
                                        {translate("Enter any notes for the facility.")}
                                    </Typography>
                                </AccordionSection>
                            </Accordion>
                        </StyledWrapper>
                    </StyledWrapper>

                    {/* Step 3 — Upload and submit */}
                    <StyledWrapper {...styles.stepRow}>
                        <Typography {...styles.stepIndicator} aria-hidden="true">
                            3
                        </Typography>
                        <StyledWrapper {...styles.stepContent}>
                            <Typography {...styles.stepHeading}>{translate("Upload and submit")}</Typography>
                            <Typography {...styles.stepDescription}>
                                {translate(
                                    "Effortlessly submit your completed Excel order template for smooth delivery to the relevant Greif plants, ensuring priority processing by our dedicated Customer Service Team.",
                                )}
                            </Typography>
                            <FileUpload
                                {...styles.fileUploader}
                                fileName={file?.name ?? ""}
                                accept=".xls,.xlsx,.csv"
                                label={translate("Upload a File")}
                                labelPosition="top"
                                placeholder={translate("Choose file.xlsx")}
                                error={incorrectFileExtension ? translate("must be .xls, .xlsx or .csv") : undefined}
                                onFileChange={handleFileChange}
                                onChange={handleFileChange}
                                uid="orderUploadFlowFileUpload"
                                data-test-selector="orderUploadFlow_fileUpload"
                            />
                            {fileUploaded && hasErrorsInFile && (
                                <StyledWrapper
                                    {...styles.fileErrorWrapper}
                                    data-test-selector="orderUploadFlow_fileErrors"
                                >
                                    <InfosBox {...styles.fileErrorBox}>
                                        <Typography {...styles.fileErrorText}>{errors}</Typography>
                                    </InfosBox>
                                    <Typography>{siteMessage("GreifOrderUpload_ErrorsInFileDescription")}</Typography>
                                    <Button
                                        {...styles.downloadFaultyFileButton}
                                        onClick={handleDownloadFaultyFile}
                                        data-test-selector="orderUploadFlow_downloadFaultyFile"
                                    >
                                        {translate("Download file with errors")}
                                    </Button>
                                </StyledWrapper>
                            )}
                            <div>
                                <Button
                                    {...styles.submitButton}
                                    disabled={isSubmitDisabled}
                                    isLoading={isLoading}
                                    onClick={handleSubmitOrder}
                                    data-test-selector="orderUploadFlow_submitOrder"
                                >
                                    {translate("Submit Order")}
                                </Button>
                            </div>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <GreifOrderUploadErrorsModal
                uploadErrorText={siteMessage("OrderUpload_UploadError")}
                rowsLimitExceededText={siteMessage("OrderUpload_RowsLimitExceeded")}
            />
            <Modal
                {...styles.confirmModal}
                headline={translate("Please confirm before placing your order")}
                isOpen={isConfirmModalOpen}
                handleClose={handleCancelConfirm}
                data-test-selector="orderUploadFlow_confirmModal"
            >
                <Typography>
                    {translate(
                        "Are you sure you want to proceed with placing your order? Please review your selections before confirming.",
                    )}
                </Typography>
                <StyledWrapper {...styles.confirmModalActions}>
                    <Button
                        {...styles.confirmModalCancelButton}
                        onClick={handleCancelConfirm}
                        data-test-selector="orderUploadFlow_confirmModal_cancel"
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.confirmModalSubmitButton}
                        onClick={handleConfirmSubmit}
                        data-test-selector="orderUploadFlow_confirmModal_submit"
                    >
                        {translate("Submit Order")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: OrderUploadFlow,
    definition: {
        displayName: "Greif Order Upload Flow",
        group: "Order Upload",
        allowedContexts: ["OrderUploadPage"],
        fieldDefinitions: [
            {
                name: fields.templateFilePath,
                displayName: "Template File",
                editorTemplate: "FilePickerField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
